import type { SelectDropdownOption } from 'src/components/common/SelectDropdown';

/** A schema definition row the picker can name. Field types stay off the label. */
export type SchemaDefinitionPickerSchema = {
  id: string;
  name: string;
  fields?: readonly { label: string; fieldName: string }[] | null;
};

/**
 * Keep a saved definition id visible until the catalog loads, so the control
 * does not fall back to "none" and persist a clear if the user clicks it.
 */
export function selectedSchemaDefinitionOption(
  options: readonly SelectDropdownOption[],
  value: string,
  noneOptionId: string,
  listReady: boolean,
  pending: { loading: string; unavailable: string },
): SelectDropdownOption {
  const match = options.find((option) => option.id === value);
  if (match) return match;
  if (value === noneOptionId) {
    return (
      options.find((option) => option.id === noneOptionId) ?? {
        id: noneOptionId,
        label: noneOptionId,
        display: noneOptionId,
      }
    );
  }
  const label = listReady ? pending.unavailable : pending.loading;
  return { id: value, label, display: label };
}

export function schemaDefinitionPickerOptions(
  schemas: readonly SchemaDefinitionPickerSchema[],
  noneOption: SelectDropdownOption,
): SelectDropdownOption[] {
  const named = [...schemas]
    .sort((left, right) =>
      left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }),
    )
    .map((schema) => ({
      id: schema.id,
      label: schema.name,
      display: schema.name,
    }));
  return [noneOption, ...named];
}

/** Human field names on a definition, for the preview under the picker. */
export function schemaDefinitionFieldSummary(
  schema: SchemaDefinitionPickerSchema,
): string {
  const labels: string[] = [];
  for (const field of schema.fields ?? []) {
    const label = field.label.trim() || field.fieldName;
    if (label.length > 0) labels.push(label);
  }
  return labels.join(', ');
}
